import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Upload, X } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
	Dropdown,
	type DropdownOption,
} from "../../components/ui/form/Dropdown";
import type { GetReadyRequiredDocument } from "../../domain/getReadyTasks/types";
import { useAnalytics } from "../../lib/analytics";
import { useClientTranslations } from "../../lib/i18n/client";
import { uploadTaskDocument } from "../../services/taskDocumentService";
import { getGetReadyTasks } from "../../services/taskService";
import type {
	GetReadyCatalogTaskInstance,
	GetReadyTasksResponse,
} from "../../types/task";
import styles from "./UploadDocumentsPage.module.css";

const ACCEPTED_MIME_TYPES = {
	"application/pdf": [".pdf"],
	"image/jpeg": [".jpg", ".jpeg"],
	"image/png": [".png"],
};
const MAX_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB
const UPLOAD_TASK_KEY = "upload_documents";
const NON_SELECTABLE_STATUSES: ReadonlySet<GetReadyRequiredDocument["status"]> =
	new Set(["uploaded", "completed"]);

function isRequiredDoc(value: unknown): value is GetReadyRequiredDocument {
	if (typeof value !== "object" || value === null) return false;
	const v = value as { key?: unknown; status?: unknown; label?: unknown };
	if (typeof v.key !== "string") return false;
	if (typeof v.status !== "string") return false;
	if (
		v.status !== "pending" &&
		v.status !== "uploaded" &&
		v.status !== "completed"
	) {
		return false;
	}
	if (v.label !== undefined && typeof v.label !== "string") return false;
	return true;
}

function extractRequiredDocs(
	response: GetReadyTasksResponse | undefined,
): GetReadyRequiredDocument[] {
	if (!response) return [];
	for (const step of response.steps) {
		for (const task of step.tasks) {
			if (task.kind !== "catalog") continue;
			const catalog = task as GetReadyCatalogTaskInstance;
			if (catalog.taskKey !== UPLOAD_TASK_KEY) continue;
			const raw = catalog.meta?.requiredDocuments;
			if (!Array.isArray(raw)) return [];
			return raw.filter(isRequiredDoc);
		}
	}
	return [];
}

export function UploadDocumentsPage() {
	const { t } = useClientTranslations("getReady");
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { logEvent } = useAnalytics();
	const [notes, setNotes] = useState("");
	const [selectedFile, setSelectedFile] = useState<File | null>(null);
	const [requiredDocumentKey, setRequiredDocumentKey] = useState<string>("");
	const [isSubmitting, setIsSubmitting] = useState(false);

	const p = useCallback(
		(key: string) => t(`taskPages.uploadDocuments.${key}`),
		[t],
	);

	const { data: tasksResponse } = useQuery({
		queryKey: ["tasks"],
		queryFn: () => getGetReadyTasks(),
	});

	const requiredDocs = useMemo(
		() => extractRequiredDocs(tasksResponse),
		[tasksResponse],
	);
	const pendingDocs = useMemo(
		() => requiredDocs.filter((d) => !NON_SELECTABLE_STATUSES.has(d.status)),
		[requiredDocs],
	);

	const effectiveKey =
		requiredDocumentKey || pendingDocs[0]?.key || requiredDocs[0]?.key || "";

	const handleClose = useCallback(() => {
		navigate(-1);
	}, [navigate]);

	const handleCancel = useCallback(() => {
		navigate(-1);
	}, [navigate]);

	const handleUpload = useCallback(async () => {
		if (!selectedFile || !effectiveKey) return;
		try {
			logEvent("get_ready_upload_document_pressed", undefined, {
				has_file: String(selectedFile !== null),
				required_document_key: effectiveKey,
			});
		} catch {
			// non-blocking
		}

		setIsSubmitting(true);
		try {
			await uploadTaskDocument({
				file: selectedFile,
				taskKey: UPLOAD_TASK_KEY,
				requiredDocumentKey: effectiveKey,
				notes: notes.trim() ? notes.trim() : undefined,
			});
			await queryClient.invalidateQueries({ queryKey: ["tasks"] });
			try {
				toast.success(p("success"));
			} catch {
				// non-blocking
			}
			navigate("/get-ready");
		} catch (error) {
			console.error("[UploadDocumentsPage] failed to upload document", error);
			try {
				toast.error(p("error"));
			} catch {
				// non-blocking
			}
		} finally {
			setIsSubmitting(false);
		}
	}, [effectiveKey, logEvent, navigate, notes, p, queryClient, selectedFile]);

	const onDrop = useCallback((acceptedFiles: File[]) => {
		if (acceptedFiles.length > 0) {
			setSelectedFile(acceptedFiles[0]);
		}
	}, []);

	const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
		onDrop,
		accept: ACCEPTED_MIME_TYPES,
		maxSize: MAX_SIZE_BYTES,
		multiple: false,
		noClick: true,
	});

	const hasFile = selectedFile !== null;
	const canSubmit = hasFile && !!effectiveKey && !isSubmitting;

	return (
		<div className={styles.page}>
			{/* Header */}
			<div className={styles.header}>
				<div className={styles.headerText}>
					<h1 className={styles.headerTitle}>{p("title")}</h1>
					<p className={styles.headerSubtitle}>{p("subtitle")}</p>
				</div>
				<button
					type="button"
					className={styles.closeButton}
					onClick={handleClose}
					aria-label={p("close")}
				>
					<X className={styles.closeIcon} aria-hidden="true" />
				</button>
			</div>

			<div className={styles.body}>
				{requiredDocs.length > 0 && (
					<section className={styles.section}>
						<span id="upload-doc-key-label" className={styles.sectionLabel}>
							{p("sections.picker.label")}
						</span>
						<Dropdown
							options={requiredDocs.map<DropdownOption>((doc) => {
								const nonSelectable = NON_SELECTABLE_STATUSES.has(doc.status);
								return {
									value: doc.key,
									label: nonSelectable
										? `${doc.label ?? doc.key} — ${p("sections.picker.uploadedSuffix")}`
										: (doc.label ?? doc.key),
									disabled: nonSelectable,
								};
							})}
							value={effectiveKey}
							onChange={setRequiredDocumentKey}
							placeholder={p("sections.picker.label")}
						/>
					</section>
				)}

				{/* Drop zone */}
				<section className={styles.section}>
					<span className={styles.sectionLabel}>
						{p("sections.files.label")}
					</span>
					<div
						{...getRootProps({
							className: `${styles.dropZone} ${isDragActive ? styles.dropZoneActive : ""} ${hasFile ? styles.dropZoneHasFile : ""}`,
						})}
					>
						<input {...getInputProps()} />
						{hasFile ? (
							<>
								<Upload className={styles.dropIcon} aria-hidden="true" />
								<span className={styles.dropHeading}>{selectedFile.name}</span>
								<span className={styles.dropSub}>
									{p("sections.files.changeFile")}
								</span>
							</>
						) : (
							<>
								<span className={styles.dropHeading}>
									{p("sections.files.drag")}
								</span>
								<span className={styles.dropSub}>{p("sections.files.or")}</span>
								<button
									type="button"
									className={styles.browseButton}
									onClick={open}
								>
									{p("sections.files.browse")}
								</button>
								<span className={styles.dropHint}>
									{p("sections.files.hint")}
								</span>
							</>
						)}
					</div>
				</section>

				{/* Notes */}
				<section className={styles.section}>
					<label htmlFor="upload-notes" className={styles.sectionLabel}>
						{p("sections.notes.label")}
					</label>
					<textarea
						id="upload-notes"
						className={styles.notesTextarea}
						placeholder={p("sections.notes.placeholder")}
						value={notes}
						onChange={(e) => setNotes(e.target.value)}
						rows={3}
					/>
				</section>

				{/* Buttons */}
				<div className={styles.buttonRow}>
					<button
						type="button"
						className={styles.cancelButton}
						onClick={handleCancel}
						disabled={isSubmitting}
					>
						{p("cancel")}
					</button>
					<button
						type="button"
						className={`${styles.uploadButton} ${hasFile ? styles.uploadButtonActive : ""}`}
						onClick={handleUpload}
						disabled={!canSubmit}
					>
						{isSubmitting ? p("uploading") : p("upload")}
					</button>
				</div>
			</div>
		</div>
	);
}
